import { useContext, useMemo } from 'react';
import { SettingsContext } from '../context/SettingsContext';
import { LockOrderContext } from '../context/LockOrderContext';
import logger from '../providers/logger'

/**
 * Use logging Application Insights logging and local trace logging.
 * @returns 
 */
export const useLogger = () => {
  const { traceLog, setTraceLog, settings } = useContext(SettingsContext);
  const {
    orderID, 
    orderLockSystemID, 
    lockSystemID, 
    ccid, 
    sapOrderNumber, 
    orderPONumber, 
    designID,
    quoteNumberForTransfer } = useContext(LockOrderContext);


    const systemIdentifier = useMemo(() => {
      return`orderID: ${orderID}, orderLockSystemID: ${orderLockSystemID}, lockSystemID: ${lockSystemID}, ccid: ${ccid}, sapOrderNumber: ${sapOrderNumber}, orderPONumber: ${orderPONumber}, designID: ${designID}, quoteNumberForTransfer: ${quoteNumberForTransfer}`
    }, [orderID, orderLockSystemID, lockSystemID, ccid, sapOrderNumber, orderPONumber, designID, quoteNumberForTransfer]);

  /**
   * add messages to the trace log
   * If debugMode is enabled with traceLogToConsole setting, also use console.log
   * @param messages 
   */
  const log = (...messages: any[]) => {
    if (settings?.debugMode && settings.traceLogToConsole) {
      console.log(...messages);
    }

    add(stringifyMessages(...messages));
  }

  /**
   * add messages to the trace log
   * If debugMode is enabled with traceLogToConsole setting, also use console.warn
   * @param messages 
   */
  const warn = (...messages: any[]) => {
    if (settings?.debugMode && settings.traceLogToConsole) {
      console.warn(...messages);
    }

    add(stringifyMessages(...messages));
  }

  /**
   * add messages to the trace log
   * If debugMode is enabled with traceLogToConsole setting, also use console.error
   * @param messages 
   */
  const error = (...messages: any[]) => {
    if (settings?.debugMode && settings.traceLogToConsole) {
      console.error(...messages);
    }

    add(stringifyMessages(...messages), true);
  }

  const stringifyMessages = (...messages: any[]) => {
    return messages.map((msg) => {
      try {
        if (msg === undefined || msg === null) {
          return typeof msg;
        }
        else if (typeof msg === 'function') {
          return `Function: ${msg.name || 'anonymous'}`; // Return the function name, or 'anonymous' if unnamed
        }
        else if (typeof msg === 'object') {
          try {
            return JSON.stringify(msg, null, 1); // Format the object as JSON
          } catch (error: any) {
            return `Error stringifying object: ${error.message}`;
          }
        } else {
          return msg.toString();
        }
      } catch (ex: any){
        return `Error converting message to string: ${ex.message}`;
      }
    });
  }

  // internal function to add messages to the trace log
  const add = (messages: string[], uploadToAI: boolean = false) => {
    const msg = messages.join();
    setTraceLog([...traceLog, msg]);
    if (uploadToAI) {
      try {
        logger.error(`${msg} :: ${systemIdentifier}`);
      }
      catch (error: any) {
        //do not fail on upload
      }
    }

  };

  /**
   * Get all trace log entries
   * @returns 
   */
  const get = () => {
    return traceLog;
  };

  /**
   * Clear all trace log entries
   * @returns 
   */
  const clear = () => {
    setTraceLog([]);
  };

  /**
   * Upload trace log entries to Application Insights
   * @returns 
   */
  const uploadTraceLogs = () => {
    try {
      logger.info(`UI Trace Log:\n${systemIdentifier}\n${traceLog.join('\n')}`);
    }
    catch (error: any) {
      //do not fail on upload
      return;
    }

    clear();
  };

  /**
   * Application Insights logging. Will add systemIdentifier to entries using info, warning, error.
   */
  const AI = {
    info: (message: string): void => { logger.info(`${message} :: ${systemIdentifier}`) },
    warning: (message: string): void => { logger.warning(`${message} :: ${systemIdentifier}`) },
    error: (message: string) => { logger.error(`${message} :: ${systemIdentifier}`) },
    event: logger.event
  };

  /**
   * Local trace logging: add, log, warn, error, get, clear, uploadTraceLogs
   */
  const UI = {log, warn, error, get, clear, uploadTraceLogs};

  return {
    AI,
    UI
  };
};